<template>
	<div class="rich-text-editor">
		<div
			id="rich-text-editor-toolbar"
			v-show="isFocused || hasContent"
			class="rich-text-editor__toolbar"
		>
			<span class="ql-formats rich-text-editor__toolbar-group">
				<button class="ql-bold" type="button" />
				<button class="ql-italic" type="button" />
			</span>

			<span class="rich-text-editor__divider" />

			<span class="ql-formats rich-text-editor__toolbar-group">
				<button class="ql-underline" type="button" />
				<button class="ql-strike" type="button" />
				<button class="ql-clean" type="button" />
			</span>

			<span class="rich-text-editor__divider" />
			<span class="ql-formats rich-text-editor__toolbar-group">
				<button
					@mouseenter="isLinkHovered = true"
					@mouseleave="isLinkHovered = false"
					type="button"
					@click="handleLinkClick"
					class="my-link-button"
				>
					<Icon
						type="link"
						:size="24"
						:color="isLinkHovered || isLinkActive ? 'var(--primary_color)' : '#333333'"
					/>
				</button>
			</span>
			<span class="rich-text-editor__divider" />
			<span class="ql-formats rich-text-editor__toolbar-group">
				<button class="ql-list" value="bullet" type="button" />
			</span>
		</div>

		<vue-editor
			ref="editor"
			v-model="localValue"
			:editorOptions="editorOptions"
			class="rich-text-editor__editor"
		/>

		<v-dialog v-model="linkDialog" max-width="600">
			<v-card class="rich-text-editor__dialog">
				<div class="rich-text-editor__dialog__title">
					{{ isEditingLink ? 'Edit Link' : 'Adding a link' }}
				</div>

				<div class="rich-text-editor__dialog__input-container">
					<span class="rich-text-editor__dialog__input-container__title"
						>Name
						<span class="rich-text-editor__dialog__input-container__title__required"
							>*</span
						>
					</span>
					<input
						class="rich-text-editor__dialog__input-container__input"
						v-model="linkText"
						type="text"
						placeholder="..."
					/>
				</div>
				<div class="rich-text-editor__dialog__input-container">
					<span class="rich-text-editor__dialog__input-container__title"
						>Link
						<span class="rich-text-editor__dialog__input-container__title__required"
							>*</span
						>
					</span>
					<input
						class="rich-text-editor__dialog__input-container__input"
						v-model="linkUrl"
						type="text"
						placeholder="https://"
					/>
				</div>
				<div class="rich-text-editor__dialog__button-container">
					<button
						class="rich-text-editor__dialog__button-container__button"
						@click="closeDialog"
					>
						Cancel
					</button>

					<button
						class="rich-text-editor__dialog__button-container__button__save"
						:disabled="!linkUrl"
						:style="{
							backgroundColor: !linkUrl ? '#9b9b9b' : 'var(--primary_color)',
						}"
						@click="insertLink"
					>
						Save
					</button>
				</div>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import { VueEditor } from 'vue2-editor'
import Quill from 'quill'
import { mapActions } from 'vuex'
import Dwh from '@/mixins/Dwh'

export default {
	name: 'RichTextEditor',
	mixins: [Dwh],
	components: {
		VueEditor,
	},

	props: {
		value: {
			type: String,
			default: '',
		},
		placeholder: {
			type: String,
			default: '',
		},
		minHeight: {
			type: [String, Number],
			default: 160,
		},
	},

	data() {
		return {
			localValue: this.value,
			linkDialog: false,
			linkUrl: '',
			linkText: '',
			savedRange: null,
			isEditingLink: false,
			isFocused: false,
			isLinkActive: false,
			isLinkHovered: false,
		}
	},

	watch: {
		localValue(val) {
			this.$emit('input', val)
		},

		value(val) {
			if (val !== this.localValue) {
				this.localValue = val
			}
		},
	},

	mounted() {
		this.initEditor()
	},

	beforeDestroy() {
		this.destroyEditorListeners()
	},
	computed: {
		hasContent() {
			const plain = this.localValue
				?.replace(/<[^>]*>/g, '')
				?.replace(/&nbsp;/g, ' ')
				?.trim()
			return !!plain
		},
		editorOptions() {
			return {
				modules: {
					toolbar: '#rich-text-editor-toolbar',
					clipboard: {
						matchers: [[Node.ELEMENT_NODE, this.cleanPastedContent]],
					},
				},
				placeholder: this.placeholder,
				formats: ['bold', 'italic', 'underline', 'strike', 'link', 'list'],
			}
		},
	},
	methods: {
		...mapActions('auth', ['showAlert']),
		dwhClickSaveLink(url) {
			this.sendTrigger({
				category: 'Create Discussion',
				action: 'click_add_link_discussion',
				module: 'Forums / Communities',
				title: 'Create Discussion',
				data: url,
			})
		},
		cleanPastedContent(node, delta) {
			const allowedAttributes = ['bold', 'italic', 'underline', 'strike', 'link', 'list']

			delta.ops = delta.ops.map((op) => {
				if (!op.attributes) {
					return op
				}

				const cleanAttributes = {}

				allowedAttributes.forEach((attr) => {
					if (op.attributes[attr]) {
						cleanAttributes[attr] = op.attributes[attr]
					}
				})

				return {
					...op,
					attributes: Object.keys(cleanAttributes).length ? cleanAttributes : undefined,
				}
			})

			return delta
		},
		initEditor() {
			const quill = this.$refs.editor?.quill

			if (!quill) return

			const toolbar = quill.getModule('toolbar')

			if (toolbar) {
				toolbar.addHandler('link', this.handleLinkClick)
			}

			quill.root.addEventListener('click', this.handleEditorClick)
			quill.root.addEventListener('mouseover', this.handleEditorMouseOver)
			quill.root.addEventListener('mouseout', this.handleEditorMouseOut)

			this.hideDefaultQuillTooltip(quill)
			quill.on('selection-change', (range) => {
				this.isFocused = !!range
			})
		},
		handleEditorMouseOver(event) {
			const linkEl = event.target.tagName === 'A' ? event.target : event.target.closest('a')
			if (linkEl) {
				this.isLinkActive = true
			}
		},

		handleEditorMouseOut(event) {
			const linkEl = event.target.tagName === 'A' ? event.target : event.target.closest('a')
			if (linkEl) {
				this.isLinkActive = false
			}
		},

		destroyEditorListeners() {
			const quill = this.$refs.editor?.quill

			if (!quill?.root) return

			quill.root.removeEventListener('click', this.handleEditorClick)
			quill.root.removeEventListener('mouseover', this.handleEditorMouseOver)
			quill.root.removeEventListener('mouseout', this.handleEditorMouseOut)
		},

		hideDefaultQuillTooltip(quill) {
			if (!quill?.theme?.tooltip) return

			quill.theme.tooltip.hide()
			quill.theme.tooltip.root.style.display = 'none'
		},

		handleLinkClick() {
			const quill = this.$refs.editor?.quill

			if (!quill) return

			const range = quill.getSelection()

			this.isEditingLink = false
			this.savedRange = range

			if (range?.length > 0) {
				this.linkText = quill.getText(range.index, range.length)
			} else {
				this.linkText = ''
			}

			this.linkUrl = ''
			this.linkDialog = true
		},
		handleEditorClick(event) {
			const target = event.target
			const linkEl = target.tagName === 'A' ? target : target.closest('a')

			if (!linkEl) return

			event.preventDefault()
			event.stopPropagation()

			const quill = this.$refs.editor?.quill

			if (!quill) return

			const blot = Quill.find(linkEl)

			if (!blot) return

			const index = quill.getIndex(blot)
			const text = linkEl.innerText || linkEl.textContent || ''
			const href = linkEl.getAttribute('href') || ''

			this.savedRange = {
				index,
				length: text.length,
			}

			this.isEditingLink = true
			this.linkText = text
			this.linkUrl = href
			this.linkDialog = true
		},

		insertLink() {
			const quill = this.$refs.editor?.quill

			if (!this.linkUrl) {
				this.showAlert({ message: 'Link cannot be blank.', type: 'error' })
				return
			}

			const urlPattern = /^(https?:\/\/|www\.)/i

			if (!urlPattern.test(this.linkUrl)) {
				this.showAlert({ message: 'Enter a valid URL.', type: 'error' })
				return
			}

			const text = this.linkText || this.linkUrl
			const normalizedUrl = this.normalizeUrl(this.linkUrl)

			if (this.isEditingLink && this.savedRange) {
				this.updateExistingLink(quill, text, normalizedUrl)
			} else {
				this.insertNewLink(quill, text, normalizedUrl)
			}
			this.dwhClickSaveLink(this.linkUrl)

			this.closeDialog()
		},

		updateExistingLink(quill, text, url) {
			quill.deleteText(this.savedRange.index, this.savedRange.length)
			quill.insertText(this.savedRange.index, text, 'link', url)
			quill.setSelection(this.savedRange.index + text.length, 0)
		},

		insertNewLink(quill, text, url) {
			const range = this.savedRange || quill.getSelection()
			const index = range ? range.index : quill.getLength()

			if (range?.length > 0) {
				quill.deleteText(range.index, range.length)
			}

			quill.insertText(index, text, 'link', url)
			quill.setSelection(index + text.length, 0)
		},

		closeDialog() {
			this.linkDialog = false
			this.linkUrl = ''
			this.linkText = ''
			this.savedRange = null
			this.isEditingLink = false
		},

		normalizeUrl(url) {
			if (!url) return ''

			if (/^(https?:\/\/|mailto:|tel:)/i.test(url)) {
				return url
			}

			return `https://${url}`
		},
	},
}
</script>

<style lang="scss">
.rich-text-editor {
	width: 100%;
	border-radius: 2px;
	background-color: #ffffff;
	font-family: Inter, sans-serif;

	&__toolbar {
		position: relative;
		z-index: 5;
		display: flex;
		align-items: center;
		min-height: 44px;
		padding: 0 12px;
		background-color: #ffffff;
		border: none !important;
		overflow: visible;

		&::-webkit-scrollbar {
			display: none;
		}
	}

	&__toolbar-group {
		display: flex;
		align-items: center;
		margin: 0 !important;
		white-space: nowrap;
	}

	&__divider {
		display: inline-block;
		width: 1px;
		height: 28px;
		margin: 0 12px;
		background-color: #eeeeee;
		flex-shrink: 0;
	}

	&__editor {
		.ql-container {
			border: none !important;
			font-family: Inter, sans-serif;
		}

		.ql-editor {
			font-weight: 400;
			font-size: 18px;
			line-height: 100%;
			letter-spacing: 0;
			min-height: 154px;
			text-decoration-style: solid;
			color: #4a4a4a;
			padding: 0;
		}

		.ql-editor.ql-blank::before {
			left: 0;
			right: 16px;
			font-style: normal;
			color: #9b9b9b;
		}

		.ql-editor a {
			color: var(--primary_color);
			text-decoration: underline;
		}

		.ql-editor strong {
			font-weight: 700;
		}

		.ql-editor p {
			margin: 0;
		}
	}

	&__dialog {
		font-family: Inter, sans-serif;
		display: flex;
		flex-direction: column;
		border-radius: 8px;
		padding: 24px;
		gap: 16px;
		&__title {
			font-weight: 700;
			font-size: 20px;
			color: #4a4a4a;
		}
		&__input-container {
			display: flex;
			flex-direction: column;
			padding: 12px;
			gap: 8px;
			border: 1px solid #e2e2e2;
			border-radius: 8px;
			&__input {
				font-weight: 400;
				font-size: 18px;
				line-height: 100%;
				letter-spacing: 0;
				outline: none;
				color: #4a4a4a;
			}
			&__title {
				font-family: Inter, sans-serif;
				font-weight: 500;
				font-size: 18px;
				line-height: 100%;
				letter-spacing: 0;
				color: #4a4a4a;
				&__required {
					color: red;
				}
			}
		}
		&__button-container {
			display: flex;
			align-items: center;
			justify-content: center;
			gap: 16px;
			&__button {
				width: 150px;
				height: 46px;
				border: 1.5px solid var(--secondary_color);
				border-radius: 8px;
				color: var(--secondary_color);
				font-weight: 700;
				font-size: 18px;
				line-height: 100%;
				letter-spacing: 0;
				text-align: center;
				&__save {
					width: 115px;
					height: 46px;
					color: #ffffff;
					font-weight: 700;
					font-size: 18px;
					border-radius: 8px;
				}
			}
		}
	}
}
.rich-text-editor {
	.ql-toolbar.ql-snow {
		padding: 0 !important;
		border: none !important;
		font-family: Inter, sans-serif !important;
	}

	.ql-container.ql-snow {
		border: none !important;
	}
}
.rich-text-editor {
	.ql-snow.ql-toolbar button,
	.ql-snow .ql-toolbar button {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 32px;
		height: 32px;
		padding: 4px;
		color: #1f1f1f;
	}

	.ql-snow.ql-toolbar button svg,
	.ql-snow .ql-toolbar button svg {
		width: 20px;
		height: 20px;
	}

	.ql-snow.ql-toolbar button:hover,
	.ql-snow.ql-toolbar button.ql-active {
		color: var(--primary_color) !important;
	}

	.ql-snow.ql-toolbar button:hover .ql-stroke,
	.ql-snow.ql-toolbar button.ql-active .ql-stroke {
		stroke: var(--primary_color) !important;
	}

	.ql-snow.ql-toolbar button:hover .ql-fill,
	.ql-snow.ql-toolbar button.ql-active .ql-fill {
		fill: var(--primary_color) !important;
	}

	.ql-snow .ql-stroke {
		stroke: currentColor;
	}

	.ql-snow .ql-fill {
		fill: currentColor;
	}

	.ql-snow .ql-even {
		fill-rule: evenodd;
	}
}
.rich-text-editor {
	.rich-text-editor__header-select.ql-header {
		width: 58px !important;
		height: 32px;
	}

	.ql-snow .ql-picker.ql-header {
		width: 58px !important;
		height: 32px;
	}
	.ql-snow .ql-picker.ql-header .ql-picker-label {
		display: flex;
		align-items: center;
		justify-content: center;
		flex-direction: row-reverse;
		gap: 4px;

		height: 32px;
		padding: 0 6px;

		border: none !important;
		color: #333333;

		font-size: 0;
	}
	.ql-snow .ql-picker.ql-header .ql-picker-label::before {
		content: '';

		width: 12px;
		height: 12px;

		background: url(~@/assets/images/chevron-down.png) center no-repeat;
		background-size: contain;
	}
	.ql-snow .ql-picker.ql-header .ql-picker-label::after {
		content: 'Aa';
		font-size: 20px;
		font-weight: 500;
		line-height: 1;
		color: #333333;
	}

	.ql-snow .ql-picker.ql-header .ql-picker-label svg {
		display: none;
	}

	.ql-snow .ql-picker.ql-header .ql-picker-options {
		position: absolute;
		top: 100%;
		left: 0;
		z-index: 50;
		min-width: 150px;
		padding: 6px 0;
		border: 1px solid #e2e2e2;
		border-radius: 4px;
		background-color: #ffffff;
		box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
	}

	.ql-snow .ql-picker.ql-header .ql-picker-item {
		padding: 6px 12px;
		font-size: 14px;
		color: #4a4a4a;
	}

	.ql-snow .ql-picker.ql-header .ql-picker-item:hover {
		background-color: #f5f5f5;
	}
}
.rich-text-editor {
	.ql-snow .ql-color-picker {
		width: 42px;
		height: 32px;
	}

	.ql-snow .ql-color-picker .ql-picker-label {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 42px;
		height: 32px;
		padding: 0;
		border: none;
	}

	.ql-snow .ql-color-picker .ql-picker-options {
		padding: 6px;
		border: 1px solid #e2e2e2;
		border-radius: 4px;
		box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
	}

	.ql-snow .ql-color-picker .ql-picker-item {
		width: 20px;
		height: 20px;
		margin: 2px;
		border-radius: 50%;
	}
}
.rich-text-editor {
	.ql-link {
		margin-left: 0;
	}
}
@media (max-width: 600px) {
	.rich-text-editor {
		&__toolbar {
			padding: 0 8px;
		}

		&__divider {
			margin: 0 8px;
		}

		&__editor {
			.ql-editor {
				font-size: 20px;
				line-height: 1.35;
			}
		}
	}
}
</style>
